import type { ChangeEvent } from 'react';
import styled from '@emotion/styled';
import Form from 'react-bootstrap/Form';

import BayBadge from '../../BayBadge';

import ClipBoardsSvg from './ClipboardsSvg';

interface Feature {
	title: string;
	stage?: string;
	description: string;
	toggle?: boolean;
	defaultValue?: boolean;
	takeMeThere?: string | false;
	takeMeThereFunc?: (() => void) | false;
	useLearnMore?: boolean;
}

interface ProductStage {
	text: string;
	color: string;
	bgColor?: string;
	bgValue?: string;
	textValue?: string;
}

interface FeatureMenuProps {
	featureList?: Record<string, Feature>;
	toggleFunc?: (id: string, checked: boolean) => void;
	productStages?: Record<string, ProductStage>;
}

const StyledDiv = styled.div`
	width: 100%;
	color: var(--bs-dark);
	padding: 0;
	margin: 0;
	padding-bottom: 32px;

	.feature-submit-button {
		text-align: center;
		padding-bottom: 24px;
		bottom: 0;
		position: fixed;
		text-align: center;
	}
`;

const StyledList = styled.ul`
	list-style: none;
	padding: 0 14px;
	margin-right: 16px;

	> li {
		margin: 0;
		padding: 16px 0 8px 0;

		&:first-of-type {
			padding-top: 8px;
		}
	}

	.feature-section {
		border-top: 1px solid #e5e5e5;
		padding-bottom: 24px;

		.feature-title {
			font-weight: 600;
			align-self: center;
			padding-right: 10px;
		}
		.feature-title-bottom {
			margin-bottom: 8px;
		}
		.feature-date-section {
			font-weight: 600;
			margin-bottom: 8px;
		}
		.feature-description {
			margin-bottom: 8px;
		}
		.feature-date,
		.feature-official-date {
			color: #9b9b9b;
		}
		.feature-date {
			padding-right: 10px;
		}
		.feature-official-date {
			padding-left: 10px;
			border-left: 2px solid #9b9b9b;
		}
		.feature-info {
			padding-left: 10px;
		}
		.feature-date-info-icon {
			font-size: 18px;
		}
		.feature-external-link {
			font-weight: 600;
			padding-left: 0px;
			padding-right: 0px;
		}
		small {
			font-size: 12px;
			color: var(--bs-gray-500);
		}
	}

	.feature-section:first-of-type {
		border-top: 0px !important;
	}

	.feature-section:last-of-type {
		border-bottom: 0px !important;
	}

	// Left inset only; the right comes from the base padding above. The 65px of
	// right padding this replaced was tuned for the old fixed 360px panel and, now
	// that the panel is fluid, was dead space holding the badge and toggle 82px in
	// from the drawer edge instead of 31px.
	@media only screen and (max-width: 480px) {
		padding-left: 16px;
	}
`;

const NoContentDiv = styled.div`
	text-align: center;
	padding: 40px 50px;
`;
const FeatureMenu = ({ featureList, toggleFunc, productStages = {} }: FeatureMenuProps) => {
	const handleCheckFeature = (e: ChangeEvent<HTMLInputElement>) => {
		toggleFunc && toggleFunc(e.target.id, e.target.checked);
	};

	const renderMenus = (feature: Feature, key: string) => {
		const stage = productStages[feature?.stage || ''];
		return (
			<li key={key} className="feature-section">
				<div className="d-flex justify-content-between align-items-center feature-title-bottom">
					{/* flex-grow-1 replaces a `max-width: 70%` cap so the title runs to
					    the badge; the badge's flex-shrink-0 keeps it from being squashed. */}
					<div className="feature-title flex-grow-1">{feature.title}</div>
					{stage && (
						<BayBadge
							bg={stage.text === 'Beta' ? stage.bgColor : stage.color}
							bgValue={stage.bgValue}
							className="flex-shrink-0"
							isLight={true}
							size="md"
							text={stage.color}
							textValue={stage.textValue}
						>
							{stage.text}
						</BayBadge>
					)}
				</div>
				{/* col + col-auto, not col-9 + col-3: the switch is ~40px, so a fixed
				    col-3 reserved a quarter of the row and wrapped the description early.
				    col takes whatever the content-sized toggle column leaves. */}
				<div className="row feature-description">
					<div className={`pe-0 ${feature.toggle ? 'col' : 'col-12'}`}>
						<p className="mb-0">{feature.description}</p>
					</div>
					{feature.toggle && (
						<div className="d-flex col-auto justify-content-end">
							<Form.Check
								defaultChecked={feature.defaultValue}
								id={key}
								type="switch"
								onChange={(e) => handleCheckFeature(e)}
							/>
						</div>
					)}
				</div>
				{(feature.takeMeThere !== false ||
					feature.takeMeThereFunc !== false) && (
					<div>
						<button
							className="feature-external-link btn btn-link"
							disabled={!feature.defaultValue && feature.toggle}
							type="button"
							onClick={() =>
								feature.takeMeThere !== false
									? window.open(feature.takeMeThere)
									: feature.takeMeThereFunc !== false
										? feature.takeMeThereFunc()
										: () => void 0
							}
						>
							{feature.useLearnMore ? 'Learn More!' : 'Take me there!'}
						</button>
					</div>
				)}
			</li>
		);
	};

	return featureList && Object.keys(featureList).length > 0 ? (
		<StyledDiv>
			<StyledList>
				{Object.keys(featureList).map((feature) => {
					return renderMenus(featureList[feature], feature);
				})}
			</StyledList>
		</StyledDiv>
	) : (
		<NoContentDiv>
			<div>
				<ClipBoardsSvg />
			</div>
			<div>There are no new updates right now. Please check back later.</div>
		</NoContentDiv>
	);
};

export default FeatureMenu;
